import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { supabase } from '../lib/supabaseClient';

const kenyaCounties = [
  "Baringo", "Bomet", "Bungoma", "Busia", "Elgeyo-Marakwet", "Embu", "Garissa",
  "Homa Bay", "Isiolo", "Kajiado", "Kakamega", "Kericho", "Kiambu", "Kilifi",
  "Kirinyaga", "Kisii", "Kisumu", "Kitui", "Kwale", "Laikipia", "Lamu",
  "Machakos", "Makueni", "Mandera", "Marsabit", "Meru", "Migori", "Mombasa",
  "Murang'a", "Nairobi", "Nakuru", "Nandi", "Narok", "Nyamira", "Nyandarua",
  "Nyeri", "Samburu", "Siaya", "Taita-Taveta", "Tana River", "Tharaka-Nithi",
  "Trans Nzoia", "Turkana", "Uasin Gishu", "Vihiga", "Wajir", "West Pokot"
];


const Register = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    phoneNumber: '',
    country: '',
    county: '',
    ageRange: '',
    program: 'EmpowerHer',
    interestAreas: [],
    experience: 'Beginner',
    agree: false,
    honeypot: ''
  });

  const [countries, setCountries] = useState([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetch('https://restcountries.com/v3.1/all')
      .then((res) => res.json())
      .then((data) => {
        const countryNames = data.map((c) => c.name.common).sort();
        setCountries(countryNames);
      })
      .catch(() => {
        setCountries(['Kenya', 'Nigeria', 'Ghana', 'Other']);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'agree') {
      setFormData({ ...formData, [name]: checked });
    } else if (type === 'checkbox') {
      setFormData((prev) => {
        const areas = new Set(prev.interestAreas);
        checked ? areas.add(value) : areas.delete(value);
        return { ...prev, interestAreas: Array.from(areas) };
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setMessage('');

  if (formData.honeypot) return;
  if (!formData.agree) return alert('You must agree to the terms.');
  if (formData.country === 'Kenya' && !formData.county) return alert('Please select a county.');

  const cleanData = {
    email: formData.email.trim().toLowerCase(),
    password: formData.password.trim(),
    full_name: formData.fullName.trim(),
  };

  // 🔍 Log the data being sent to Supabase Auth
  console.log("🟢 Signup Payload:");
  console.log("Email:", cleanData.email);
  console.log("Password:", cleanData.password);
  console.log("Full Name:", cleanData.full_name);

  // Step 1: Auth signup
 const { error: signUpError } = await supabase.auth.signUp({
  email: cleanData.email,
  password: cleanData.password,
  options: {
    data: {
      full_name: cleanData.full_name,
    },
  },
});


  if (signUpError && signUpError.status !== 429) {
    console.error("❌ Supabase signup error:", signUpError);
    return alert(`Signup error: ${signUpError.message || 'Unknown error'}`);
  }

 const {
  data: { user },
  error: getUserError,
} = await supabase.auth.getUser();

if (getUserError || !user) {
  console.error("❌ Failed to retrieve user after signup:", getUserError);
  setMessage('Signup complete, but we couldn’t confirm your account immediately. Please try logging in.');
  return;
}

const userId = user.id;


  // Step 2: Insert into profiles
  const { error: profileError } = await supabase.from('profiles').upsert({
    id: userId,
    full_name: cleanData.full_name,
    email: cleanData.email,
    role: 'User',
    age_range: formData.ageRange,
    country: formData.country,
    county: formData.county,
    created_at: new Date().toISOString(),
  });

  if (profileError) {
    console.error("❌ Failed to update profile:", profileError);
    alert('Account created but failed to complete profile.');
  }

  // Step 3: Insert into registrations
  const { error: registrationError } = await supabase.from('registrations').insert({
    user_id: userId,
    full_name: cleanData.full_name,
    email: cleanData.email,
    country: formData.country,
    county: formData.county || null,
    age_range: formData.ageRange,
    program: formData.program,
    interest_areas: formData.interestAreas,
    experience: formData.experience,
    phone_number: formData.phoneNumber,
    created_at: new Date().toISOString(),
  });

  if (registrationError) {
    console.error('❌ Failed to save registration:', registrationError);
    alert('Signup error: Database error saving new user');
    return;
  }

  setMessage('🎉 Registration successful! Check your email to verify your account.');
  setFormData({
    fullName: '', email: '', password: '', phoneNumber: '', country: '', county: '', ageRange: '',
    program: 'EmpowerHer', interestAreas: [], experience: 'Beginner', agree: false, honeypot: ''
  });
};


  
  return (
    <main className="min-h-screen bg-gradient-to-bl from-blue-50 to-indigo-100 p-6">
      <section className="max-w-3xl mx-auto py-12">
        <h1 className="text-4xl font-extrabold text-indigo-800 mb-8 text-center">
          Register for EmpowerHer / Girls-To-Tech
        </h1>
        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow space-y-6">

          {/* Full Name */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Full Name</label>
            <input
              name="fullName"
              type="text"
              value={formData.fullName}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-4 py-2"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Email Address</label>
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-4 py-2"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Password</label>
            <input
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-4 py-2"
              required
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Phone Number</label>
           <PhoneInput
              country={'ke'}
              value={formData.phoneNumber}
              onChange={(phone) => setFormData({ ...formData, phoneNumber: `+${phone}` })}
              inputStyle={{
                width: '100%',
                paddingLeft: '48px', // Ensures number isn't hidden behind flag dropdown
                height: '42px',
                fontSize: '1rem',
                borderRadius: '0.375rem',
                border: '1px solid #D1D5DB',
              }}
              buttonStyle={{
                borderTopLeftRadius: '0.375rem',
                borderBottomLeftRadius: '0.375rem',
                border: '1px solid #D1D5DB',
              }}
              containerStyle={{ width: '100%' }}
              inputProps={{
                name: 'phone',
                required: true,
              }}
            />

          </div>

          {/* Country & County */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Country</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded px-4 py-2"
                required
              >
                <option value="">Select Country</option>
                {countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {formData.country === 'Kenya' && (
              <div>
                <label className="block text-gray-700 font-semibold mb-2">County</label>
                <select
                  name="county"
                  value={formData.county}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded px-4 py-2"
                  required
                >
                  <option value="">Select County</option>
                  {kenyaCounties.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Age Range */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Age Range</label>
            <select
              name="ageRange"
              value={formData.ageRange}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-4 py-2"
              required
            >
              <option value="">Select Age Range</option>
              <option value="Under 18">Under 18</option>
              <option value="18-24">18–24</option>
              <option value="25-34">25–34</option>
              <option value="35+">35+</option>
            </select>
          </div>

          {/* Program of Interest */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Program of Interest</label>
            <select
              name="program"
              value={formData.program}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-4 py-2"
            >
              <option value="EmpowerHer">EmpowerHer</option>
              <option value="Girls-To-Tech">Girls-To-Tech</option>
            </select>
          </div>

          {/* Interest Area for Girls-To-Tech */}
          {formData.program === 'Girls-To-Tech' && (
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Interest Area</label>
              <div className="flex flex-wrap gap-4">
                {['Cloud Computing', 'Data Science', 'Cybersecurity', 'Data Engineering'].map((area) => (
                  <label key={area} className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="interestAreas"
                      value={area}
                      checked={formData.interestAreas.includes(area)}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    {area}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Experience Level */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Prior Experience Level</label>
            <select
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-4 py-2"
            >
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
            </select>
          </div>

          {/* Agreement */}
          <div className="flex items-center">
            <input
              name="agree"
              type="checkbox"
              checked={formData.agree}
              onChange={handleChange}
              className="mr-2"
            />
            <span className="text-gray-700">I agree to the terms and privacy policy</span>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="w-full bg-indigo-700 hover:bg-indigo-800 text-white font-bold py-3 rounded-xl transition"
          >
            Submit Registration
          </button>

          {message && <p className="text-green-700 font-medium mt-4">{message}</p>}

          <input
  type="text"
  name="honeypot"
  value={formData.honeypot}
  onChange={handleChange}
  autoComplete="off"
  style={{ display: 'none' }}
/>

        </form>
      </section>
    </main>
  );
};

export default Register;